import api from '../../../services/api'

export interface GovRfqData {
  id?: string
  title: string
  description: string
  agencyId: string
  agencyName: string
  category: string
  budget: number
  deadline: string
  requirements: string[]
  complianceRequirements: {
    cageCode: boolean
    dunsNumber: boolean
    gsaSchedule: boolean
    certifications: string[]
    securityClearance: string
    pastPerformance: boolean
  }
  solicitationNumber: string
  naicsCode: string
  setAside: string
  contractType: 'fixed-price' | 'cost-plus' | 'time-and-materials' | 'indefinite-delivery'
  status: 'draft' | 'published' | 'closed' | 'awarded'
  createdAt?: string
  updatedAt?: string
}

export interface GovProposalData {
  id?: string
  rfqId: string
  vendorId: string
  vendorName: string
  responseText: string
  proposedPrice: number
  deliveryDays: number
  cageCode: string
  dunsNumber: string
  gsaScheduleId?: string
  certifications: string[]
  complianceStatement: string
  pastPerformance: string
  securityClearance: string
  teamQualifications: string
  status: 'draft' | 'submitted' | 'under-review' | 'accepted' | 'rejected'
  submittedAt?: string
  reviewedAt?: string
}

export class GovRfqApi {
  /**
   * Create a new government RFQ
   */
  static async createRfq(rfqData: {
    title: string
    description: string
    budgetRange: string
    location: string
    deadline: string
  }): Promise<GovRfqData> {
    const response = await api.post('/api/gov/rfqs', rfqData)
    return (response as any).data
  }

  /**
   * Get all government RFQs
   */
  static async getRfqs(filters?: {
    status?: string
    category?: string
    agencyId?: string
    setAside?: string
    contractType?: string
    page?: number
    limit?: number
  }): Promise<{ rfqs: GovRfqData[]; total: number; page: number; limit: number }> {
    const params = new URLSearchParams()
    if (filters?.status) params.append('status', filters.status)
    if (filters?.category) params.append('category', filters.category)
    if (filters?.agencyId) params.append('agencyId', filters.agencyId)
    if (filters?.setAside) params.append('setAside', filters.setAside)
    if (filters?.contractType) params.append('contractType', filters.contractType)
    if (filters?.page) params.append('page', filters.page.toString())
    if (filters?.limit) params.append('limit', filters.limit.toString())

    const response = await api.get(`/api/gov/rfqs?${params.toString()}`)
    return (response as any).data
  }

  /**
   * Get a specific government RFQ
   */
  static async getRfq(rfqId: string): Promise<GovRfqData> {
    const response = await api.get(`/api/gov/rfqs/${rfqId}`)
    return (response as any).data
  }

  /**
   * Update a government RFQ
   */
  static async updateRfq(rfqId: string, rfqData: Partial<GovRfqData>): Promise<GovRfqData> {
    const response = await api.put(`/api/gov/rfqs/${rfqId}`, rfqData)
    return (response as any).data
  }

  /**
   * Delete a government RFQ
   */
  static async deleteRfq(rfqId: string): Promise<{ success: boolean }> {
    const response = await api.delete(`/api/gov/rfqs/${rfqId}`)
    return (response as any).data
  }

  /**
   * Publish a government RFQ
   */
  static async publishRfq(rfqId: string): Promise<GovRfqData> {
    const response = await api.post(`/api/gov/rfqs/${rfqId}/publish`)
    return (response as any).data
  }

  /**
   * Close a government RFQ
   */
  static async closeRfq(rfqId: string): Promise<GovRfqData> {
    const response = await api.post(`/api/gov/rfqs/${rfqId}/close`)
    return (response as any).data
  }

  /**
   * Submit a government proposal
   */
  static async submitProposal(proposalData: Omit<GovProposalData, 'id' | 'submittedAt' | 'reviewedAt'>): Promise<GovProposalData> {
    const response = await api.post('/api/gov/proposals', proposalData)
    return (response as any).data
  }

  /**
   * Get proposals for a specific RFQ
   */
  static async getRfqProposals(rfqId: string): Promise<GovProposalData[]> {
    const response = await api.get(`/api/gov/rfqs/${rfqId}/proposals`)
    return (response as any).data
  }

  /**
   * Get vendor's proposals
   */
  static async getVendorProposals(vendorId: string, filters?: {
    status?: string
    rfqId?: string
    page?: number
    limit?: number
  }): Promise<{ proposals: GovProposalData[]; total: number; page: number; limit: number }> {
    const params = new URLSearchParams()
    if (filters?.status) params.append('status', filters.status)
    if (filters?.rfqId) params.append('rfqId', filters.rfqId)
    if (filters?.page) params.append('page', filters.page.toString())
    if (filters?.limit) params.append('limit', filters.limit.toString())

    const response = await api.get(`/api/gov/proposals/vendor/${vendorId}?${params.toString()}`)
    return (response as any).data
  }

  /**
   * Get a specific proposal
   */
  static async getProposal(proposalId: string): Promise<GovProposalData> {
    const response = await api.get(`/api/gov/proposals/${proposalId}`)
    return (response as any).data
  }

  /**
   * Update proposal status (agency only)
   */
  static async updateProposalStatus(proposalId: string, status: string, reviewNotes?: string): Promise<GovProposalData> {
    const response = await api.put(`/api/gov/proposals/${proposalId}/status`, {
      status,
      reviewNotes
    })
    return (response as any).data
  }

  /**
   * Award contract to a proposal
   */
  static async awardContract(proposalId: string, awardData: {
    contractValue: number
    contractNumber: string
    startDate: string
    endDate: string
    awardNotes?: string
  }): Promise<{ success: boolean; contractId: string }> {
    const response = await api.post(`/api/gov/proposals/${proposalId}/award`, awardData)
    return (response as any).data
  }

  /**
   * Get government RFQ statistics
   */
  static async getRfqStats(agencyId?: string): Promise<{
    totalRfqs: number
    activeRfqs: number
    closedRfqs: number
    awardedRfqs: number
    totalProposals: number
    averageProposalsPerRfq: number
  }> {
    const params = agencyId ? `?agencyId=${agencyId}` : ''
    const response = await api.get(`/api/gov/rfqs/stats${params}`)
    return (response as any).data
  }

  /**
   * Search government RFQs
   */
  static async searchRfqs(query: string, filters?: {
    category?: string
    setAside?: string
    contractType?: string
    budgetMin?: number
    budgetMax?: number
    deadlineAfter?: string
    deadlineBefore?: string
  }): Promise<GovRfqData[]> {
    const searchData = {
      query,
      ...filters
    }
    const response = await api.post('/api/gov/rfqs/search', searchData)
    return (response as any).data
  }
}